import { useEffect, useRef, useState } from 'react';
import type { Operaciones } from '../../../api/operaciones';
import { ErrorApi } from '../../../api/problema';
import type { ImportacionCatalogo, LineaImportacionCatalogo } from '../../../api/ops/servicios';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Ayuda } from '../../../componentes/Ayuda';
import { Boton } from '../../../componentes/Boton';
import { Filtros } from '../../../componentes/Filtros';
import { Icono } from '../../../componentes/Icono';
import { Interruptor } from '../../../componentes/Interruptor';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { Pasos } from '../../../componentes/Pasos';
import { cifra, kilobytes } from '../../../logica/fechasCortas';
import { textoDeError } from '../../agenda/logica';
import { AYUDA } from '../textos';
import { FILTROS_IMPORTAR, TEXTOS_IMPORTAR as T, descargarPlantillaCatalogo, elegidasPorDefecto, lineasDe, resumenImportacion, type FiltroImportar } from './logica';
import '../../clientes/importar/importar.css';

/** Cada cuánto se mira cómo va una importación que se está comprobando. */
export const ESPERA_SONDEO_MS = 1500;
const MAX_BYTES = 2 * 1024 * 1024;

type Fase = 'editar' | 'subiendo' | 'fallo' | 'comprobando' | 'revisar' | 'importando' | 'hecha' | 'no-valido' | 'caducada';

function faseDe(i: ImportacionCatalogo): Fase {
    switch (i.status) {
        case 'validating':
            return 'comprobando';
        case 'invalid':
            return 'no-valido';
        case 'ready':
            return 'revisar';
        case 'applied':
            return 'hecha';
        case 'discarded':
        case 'expired':
            return 'caducada';
    }
}

interface Props {
    ops: Operaciones;
    negocio: string;
    movil: boolean;
    abierto: boolean;
    onCerrar: () => void;
    /** Ya importado: la lista de servicios y profesionales se vuelve a leer. */
    onImportado: () => void;
    onVerProfesionales: () => void;
}

/**
 * 9.5 «Importar el catálogo» (F1: solo un CSV con la plantilla, sin conectarse al programa anterior; D-040), en RV Panel de
 * ficha con RV Pasos como 2.6: Archivo · Elegir qué importar. `createCatalogImport` sube el archivo y devuelve la vista
 * previa sin crear nada (mientras se comprueba, se mira cómo va); se eligen las líneas que se crean (por defecto, todas) y
 * `applyCatalogImport` las importa en una sola transacción. Lo que ya existe no se duplica y los profesionales llegan
 * desactivados y sin acceso. Una vista previa caducada (24 h) o ya aplicada responde `conflict`: se vuelve a subir.
 */
export function ImportarCatalogo({ ops, negocio, movil, abierto, onCerrar, onImportado, onVerProfesionales }: Props) {
    const avisar = useAvisoBreve();
    const entrada = useRef<HTMLInputElement>(null);
    const [archivo, setArchivo] = useState<File | null>(null);
    const [errorArchivo, setErrorArchivo] = useState<string | null>(null);
    const [arrastrando, setArrastrando] = useState(false);
    const [importacion, setImportacion] = useState<ImportacionCatalogo | null>(null);
    const [subiendo, setSubiendo] = useState(false);
    // 9.5 · `createCatalogImport` falla por la conexión o un 5xx: no se ha subido nada y el archivo sigue elegido.
    const [fallo, setFallo] = useState<{ codigo: string | null } | null>(null);
    const [importando, setImportando] = useState(false);
    const [elegidas, setElegidas] = useState<number[]>([]);
    const [filtro, setFiltro] = useState<FiltroImportar>('servicios');
    const [estabaAbierto, setEstabaAbierto] = useState(abierto);

    if (abierto !== estabaAbierto) {
        setEstabaAbierto(abierto);
        if (abierto) {
            setArchivo(null);
            setErrorArchivo(null);
            setImportacion(null);
            setFallo(null);
            setElegidas([]);
            setFiltro('servicios');
        }
    }

    const fase: Fase = importacion === null ? (subiendo ? 'subiendo' : fallo !== null ? 'fallo' : 'editar') : importando ? 'importando' : faseDe(importacion);
    const id = importacion?.id ?? null;
    const estado = importacion?.status ?? null;

    useEffect(() => {
        if (id === null || estado !== 'validating') {
            return;
        }
        let vivo = true;
        const reloj = setTimeout(() => {
            ops.getCatalogImport(negocio, id)
                .then((i) => {
                    if (vivo) {
                        setImportacion(i);
                        if (i.status === 'ready') {
                            setElegidas(elegidasPorDefecto(i));
                        }
                    }
                })
                .catch(() => {
                    if (vivo) {
                        setImportacion((x) => (x === null ? x : { ...x }));
                    }
                });
        }, ESPERA_SONDEO_MS);
        return () => {
            vivo = false;
            clearTimeout(reloj);
        };
    }, [ops, negocio, id, estado, importacion]);

    const elegirArchivo = (f: File | undefined) => {
        if (f === undefined) {
            return;
        }
        if (!/\.csv$/i.test(f.name)) {
            setErrorArchivo(T.noCsv);
            return;
        }
        if (f.size > MAX_BYTES) {
            setErrorArchivo(T.grande);
            return;
        }
        setArchivo(f);
        setErrorArchivo(null);
    };

    const subir = () => {
        if (archivo === null) {
            setErrorArchivo(T.faltaArchivo);
            return;
        }
        setSubiendo(true);
        setFallo(null);
        ops.createCatalogImport(negocio, archivo, archivo.name)
            .then((i) => {
                setImportacion(i);
                setFiltro('servicios');
                if (i.status === 'ready') {
                    setElegidas(elegidasPorDefecto(i));
                }
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.estado < 500) {
                    avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido subir el archivo. Prueba de nuevo.') });
                } else {
                    setFallo({ codigo: e instanceof ErrorApi ? (e.problema.correlation_id ?? null) : null });
                }
            })
            .finally(() => {
                setSubiendo(false);
            });
    };

    const importar = () => {
        if (importacion === null || elegidas.length === 0) {
            return;
        }
        setImportando(true);
        ops.applyCatalogImport(negocio, importacion.id, elegidas)
            .then((i) => {
                setImportacion(i);
                onImportado();
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido importar. Prueba de nuevo.') });
            })
            .finally(() => {
                setImportando(false);
            });
    };

    const plantilla = () => {
        descargarPlantillaCatalogo(ops, negocio).catch((e: unknown) => {
            avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido descargar la plantilla. Prueba de nuevo.') });
        });
    };

    const volverAEmpezar = () => {
        setImportacion(null);
        setFallo(null);
        setArchivo(null);
        setElegidas([]);
    };

    const resumen = importacion === null ? null : resumenImportacion(importacion, elegidas);

    const pasoArchivo = (
        <div className="rv-ca-cuerpo rv-imp-paso">
            <div className="rv-imp-plantilla">
                <span className="rv-imp-plantilla__baldosa">
                    <Icono nombre="file-spreadsheet" />
                </span>
                <span className="rv-imp-plantilla__texto">
                    <strong>{T.plantillaTitulo}</strong>
                    <span>{T.plantillaTexto}</span>
                </span>
                <Boton tipo="secundario" icono="download" onClick={plantilla}>
                    Descargar plantilla
                </Boton>
            </div>
            {archivo === null ? (
                <div
                    className={`rv-imp-soltar${arrastrando ? ' rv-imp-soltar--encima' : ''}`}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setArrastrando(true);
                    }}
                    onDragLeave={() => {
                        setArrastrando(false);
                    }}
                    onDrop={(e) => {
                        e.preventDefault();
                        setArrastrando(false);
                        elegirArchivo(e.dataTransfer.files[0]);
                    }}
                >
                    <span className="rv-imp-soltar__baldosa">
                        <Icono nombre="upload" />
                    </span>
                    <strong>Elige el archivo CSV</strong>
                    <span>{movil ? T.elegirAyudaMovil : T.elegirAyuda}</span>
                    <Boton tipo="secundario" icono="file-spreadsheet" movil={movil} onClick={() => entrada.current?.click()}>
                        Elegir archivo
                    </Boton>
                </div>
            ) : (
                <div className="rv-imp-elegido">
                    <span className="rv-ca-baldosa">
                        <Icono nombre="file-spreadsheet" />
                    </span>
                    <span className="rv-ca-fila__texto">
                        <strong>{archivo.name}</strong>
                        <span>{`${kilobytes(archivo.size)} · listo para revisar`}</span>
                    </span>
                    <Boton tipo="secundario" icono="rotate-ccw" onClick={() => entrada.current?.click()}>
                        Cambiar archivo
                    </Boton>
                </div>
            )}
            <input
                ref={entrada}
                type="file"
                accept=".csv,text/csv"
                className="rv-sr"
                aria-label="Elegir archivo CSV"
                tabIndex={-1}
                onChange={(e) => {
                    elegirArchivo(e.target.files?.[0]);
                    e.target.value = '';
                }}
            />
            {errorArchivo !== null && (
                <span role="alert" className="rv-campo__error">
                    <Icono nombre="circle-alert" />
                    <span>{errorArchivo}</span>
                </span>
            )}
            <p className="rv-ca-nota rv-ca-nota--gris">
                <Icono nombre="shield-check" />
                <span>{T.soloArchivo}</span>
            </p>
        </div>
    );

    const trabajando = (
        <div className="rv-imp-trabajando" role="status">
            <Icono nombre="loader-circle" className="rv-gira" />
            <strong>{fase === 'importando' ? `Importando ${cifra(elegidas.length)} ${elegidas.length === 1 ? 'elemento' : 'elementos'}…` : 'Comprobando el archivo…'}</strong>
            <span>{fase === 'importando' ? T.importandoTexto : T.comprobandoTexto}</span>
        </div>
    );

    const cuerpo = (() => {
        switch (fase) {
            case 'editar':
                return pasoArchivo;
            case 'fallo':
                return (
                    <div className="rv-ca-cuerpo rv-imp-paso">
                        <div className="rv-imp-fallo" role="alert">
                            <strong><Icono nombre="triangle-alert" />No he podido comprobar el archivo</strong>
                            <span>Puede ser la conexión. No se ha importado nada y tu archivo sigue elegido: prueba de nuevo.</span>
                            <div className="rv-imp-botones">
                                <Boton icono="refresh-cw" onClick={subir}>
                                    Reintentar
                                </Boton>
                                <Boton tipo="secundario" icono="upload" onClick={volverAEmpezar}>
                                    Elegir otro archivo
                                </Boton>
                            </div>
                            {fallo?.codigo != null && <small>{`Código para soporte: ${fallo.codigo}`}</small>}
                        </div>
                    </div>
                );
            case 'subiendo':
            case 'comprobando':
            case 'importando':
                return trabajando;
            case 'no-valido':
                return (
                    <div className="rv-ca-cuerpo rv-imp-paso">
                        <div className="rv-ca-caja rv-ca-caja--error" role="alert">
                            <span className="rv-ca-caja__fila">
                                <Icono nombre="circle-alert" />
                                <span>
                                    <strong>El archivo no sigue la plantilla</strong>
                                    <br />
                                    {(importacion?.errors ?? []).map((e) => e.message ?? '').filter((x) => x !== '').join(' ') || T.noValidoTexto}
                                </span>
                            </span>
                        </div>
                        <div className="rv-imp-botones">
                            <Boton tipo="secundario" icono="upload" onClick={volverAEmpezar}>
                                Elegir otro archivo
                            </Boton>
                        </div>
                    </div>
                );
            case 'caducada':
                return (
                    <div className="rv-ca-cuerpo rv-imp-paso">
                        <p className="rv-ca-nota">
                            <Icono nombre="info" />
                            <span>{T.caducada}</span>
                        </p>
                        <div className="rv-imp-botones">
                            <Boton tipo="secundario" icono="upload" onClick={volverAEmpezar}>
                                Elegir otro archivo
                            </Boton>
                        </div>
                    </div>
                );
            default:
                return importacion === null || resumen === null ? null : (
                    <Revision
                        importacion={importacion}
                        resumen={resumen}
                        hecha={fase === 'hecha'}
                        movil={movil}
                        filtro={filtro}
                        elegidas={elegidas}
                        onFiltro={setFiltro}
                        onElegidas={setElegidas}
                        onVerProfesionales={onVerProfesionales}
                        onVerServicios={onCerrar}
                    />
                );
        }
    })();

    const enPasos = fase === 'editar' || fase === 'subiendo' || fase === 'fallo' || fase === 'comprobando' || fase === 'revisar' || fase === 'importando';
    const paso = fase === 'editar' ? 0 : 1;
    const n = elegidas.length;

    return (
        <PanelLateral
            abierto={abierto}
            onCerrar={onCerrar}
            movil={movil}
            tamano="ficha"
            titulo="Importar el catálogo"
            eyebrow="Importación"
            subtitulo={archivo !== null ? `${archivo.name} · ${kilobytes(archivo.size)}` : 'CSV con la plantilla de Reservaria, hasta 2 MB'}
            icono="upload"
        >
            <div className="rv-imp-panel">
                <div className="rv-imp-panel__cab">
                    {fase !== 'hecha' ? <p className="rv-ca-ceja">{paso === 0 ? 'Paso 1 de 2 · Archivo' : 'Paso 2 de 2 · Elegir qué importar'}</p> : <span />}
                    <Ayuda tarjetas={AYUDA.importar} />
                </div>
                {abierto && cuerpo}
                {abierto && enPasos && (
                    <div className="rv-imp-pie">
                        <Pasos
                            total={2}
                            valor={paso}
                            movil={movil}
                            siguiente="Subir y revisar"
                            terminar={`Importar ${cifra(n)}${movil ? '' : n === 1 ? ' elemento' : ' elementos'}`}
                            icono="upload"
                            inactivo={(paso === 0 && archivo === null) || (paso === 1 && (fase !== 'revisar' || n === 0))}
                            onInactivo={() => {
                                if (paso === 0) {
                                    setErrorArchivo(T.faltaArchivo);
                                }
                            }}
                            ocupado={fase === 'subiendo' || fase === 'comprobando' || fase === 'importando'}
                            ocupadoTexto={fase === 'importando' ? 'Importando…' : 'Subiendo…'}
                            sinVolver={paso === 1}
                            onCambio={(v) => {
                                if (v === 1 && paso === 0) {
                                    subir();
                                }
                            }}
                            onFin={importar}
                        />
                    </div>
                )}
            </div>
        </PanelLateral>
    );
}

function Revision({
    importacion,
    resumen,
    hecha,
    movil,
    filtro,
    elegidas,
    onFiltro,
    onElegidas,
    onVerProfesionales,
    onVerServicios,
}: {
    importacion: ImportacionCatalogo;
    resumen: ReturnType<typeof resumenImportacion>;
    hecha: boolean;
    movil: boolean;
    filtro: FiltroImportar;
    elegidas: number[];
    onFiltro: (f: FiltroImportar) => void;
    onElegidas: (l: number[]) => void;
    onVerProfesionales: () => void;
    onVerServicios: () => void;
}) {
    const cuenta = (f: FiltroImportar) => (f === 'errores' ? importacion.errors.length : lineasDe(importacion, f).length);
    const elegibles = filtro === 'servicios' || filtro === 'profesionales' ? lineasDe(importacion, filtro) : [];
    const lineasElegibles = elegibles.map((l) => l.line);
    const todas = lineasElegibles.length > 0 && lineasElegibles.every((l) => elegidas.includes(l));
    const tarjetas: [string, number, string, boolean][] = [
        ['Servicios nuevos', hecha ? resumen.serviciosElegidos : resumen.servicios, hecha ? 'Importados' : 'Se crean con su versión 1', true],
        ['Profesionales nuevos', hecha ? resumen.profesionalesElegidos : resumen.profesionales, 'Desactivados y sin acceso', false],
        ['Ya existen', resumen.existen, 'No se duplican', false],
        ['Con errores', resumen.errores, 'No se importan', false],
    ];
    const nota = filtro === 'errores' ? 'Corrige estas líneas en tu archivo y súbelo otra vez: lo ya importado no se duplica.' : filtro === 'existen' ? 'Se reconocen por el nombre. Si quieres cambiarlos, hazlo en su ficha.' : filtro === 'profesionales' ? 'Llegan desactivados y sin acceso al panel, con el horario del negocio. Al activarlos se aplica el límite de tu plan.' : 'Las variantes se importan como una pregunta de una respuesta, con sus minutos y euros. La vista previa caduca a las 24 horas.';
    const icono = (l: LineaImportacionCatalogo) => (l.kind === 'service' ? 'scissors' : 'contact');
    const total = resumen.serviciosElegidos + resumen.profesionalesElegidos;

    return (
        <div className="rv-ca-cuerpo rv-imp-paso">
            {hecha && (
                <div className="rv-imp-hecha" role="status">
                    <span className="rv-ca-hecho__baldosa">
                        <Icono nombre="check" />
                    </span>
                    <span className="rv-ca-fila__texto">
                        <strong>{`${cifra(total)} ${total === 1 ? 'elemento importado' : 'elementos importados'}`}</strong>
                        <span>
                            {(resumen.profesionalesElegidos === 1 ? 'El profesional llega desactivado y sin acceso: actívalo en Profesionales cuando quieras, según tu plan.' : resumen.profesionalesElegidos > 1 ? `Los ${String(resumen.profesionalesElegidos)} profesionales llegan desactivados y sin acceso: actívalos en Profesionales cuando quieras, según tu plan.` : 'Ya están en tu catálogo.') +
                                (resumen.serviciosElegidos > 0 ? ' Los servicios se pueden pedir en cuanto alguien activo los haga.' : '')}
                        </span>
                    </span>
                </div>
            )}
            <div className={`rv-imp-tarjetas${movil ? ' rv-imp-tarjetas--movil' : ''}`}>
                {tarjetas.map(([etiqueta, n, linea, oscura]) => (
                    <div key={etiqueta} className={`rv-imp-tarjeta${oscura ? ' rv-imp-tarjeta--oscura' : ''}`}>
                        <span className="rv-imp-tarjeta__cab">
                            <span>{etiqueta}</span>
                        </span>
                        <strong>{cifra(n)}</strong>
                        <span>{linea}</span>
                    </div>
                ))}
            </div>
            {hecha ? (
                <div className="rv-imp-botones">
                    <Boton tipo="secundario" icono="contact" onClick={onVerProfesionales}>
                        Ver profesionales
                    </Boton>
                    <Boton icono="scissors" onClick={onVerServicios}>
                        Ver servicios
                    </Boton>
                </div>
            ) : (
                <div className="rv-ca-bloque">
                    <Filtros
                        etiqueta="Filtrar líneas"
                        items={FILTROS_IMPORTAR.map((f) => ({ etiqueta: f.texto, cifra: cuenta(f.clave) }))}
                        valor={Math.max(0, FILTROS_IMPORTAR.findIndex((f) => f.clave === filtro))}
                        onCambio={(i) => {
                            onFiltro(FILTROS_IMPORTAR[i]?.clave ?? 'servicios');
                        }}
                    />
                    {elegibles.length > 0 && (
                        <div className="rv-sv-elegir">
                            <span>{`${String(lineasElegibles.filter((l) => elegidas.includes(l)).length)} de ${String(lineasElegibles.length)} elegidos`}</span>
                            <Boton tipo="secundario" icono={todas ? 'x' : 'check-check'} onClick={() => { onElegidas(todas ? elegidas.filter((l) => !lineasElegibles.includes(l)) : [...new Set([...elegidas, ...lineasElegibles])]); }}>
                                {todas ? 'Quitar todos' : 'Elegir todos'}
                            </Boton>
                        </div>
                    )}
                    <div role="list" aria-label="Líneas">
                        {filtro === 'errores'
                            ? importacion.errors.map((e) => (
                                  <div key={`${String(e.line)}-${e.field ?? ''}`} role="listitem" className="rv-imp-linea rv-imp-linea--error">
                                      <span className="rv-imp-linea__baldosa">
                                          <Icono nombre="circle-alert" />
                                      </span>
                                      <span className="rv-ca-fila__texto">
                                          <span className="rv-imp-linea__cab">
                                              <strong>{`Línea ${String(e.line)}`}</strong>
                                              {e.field !== undefined && <span>{` · ${e.field}`}</span>}
                                          </span>
                                          <span className="rv-imp-linea__texto">{e.message ?? ''}</span>
                                      </span>
                                  </div>
                              ))
                            : filtro === 'existen'
                              ? lineasDe(importacion, 'existen').map((l) => (
                                    <div key={l.line} role="listitem" className="rv-imp-linea">
                                        <span className="rv-imp-linea__baldosa">
                                            <Icono nombre={icono(l)} />
                                        </span>
                                        <span className="rv-ca-fila__texto">
                                            <span className="rv-imp-linea__cab">
                                                <strong>{`${l.name} · línea ${String(l.line)}`}</strong>
                                            </span>
                                            <span className="rv-imp-linea__texto">{l.detail ?? ''}</span>
                                        </span>
                                    </div>
                                ))
                              : elegibles.map((l) => (
                                    <div key={l.line} role="listitem">
                                        <Interruptor
                                            etiqueta={l.name}
                                            ayuda={`Línea ${String(l.line)}${l.detail === undefined ? '' : ` · ${l.detail}`}`}
                                            icono={icono(l)}
                                            valor={elegidas.includes(l.line)}
                                            onChange={(v) => { onElegidas(v ? [...elegidas, l.line] : elegidas.filter((x) => x !== l.line)); }}
                                        />
                                    </div>
                                ))}
                        {cuenta(filtro) === 0 && <span className="rv-ca-nota rv-ca-nota--gris">No hay líneas de este tipo.</span>}
                    </div>
                    <p className="rv-ca-nota rv-ca-nota--gris">{nota}</p>
                </div>
            )}
        </div>
    );
}
